/**
* @copyright Microsoft 2020
*/
var _a, _b;
import { __extends } from "tslib";
import dynamicProto from "@microsoft/dynamicproto-js";
import { PropertiesPluginIdentifier } from "@microsoft/applicationinsights-common";
import { BaseTelemetryPlugin, _throwInternal, arrForEach, cfgDfFunc, cfgDfMerge, cfgDfString, cfgDfValidate, createProcessTelemetryContext, dumpObj, getExceptionName, isNullOrUndefined, onConfigChange, throwError, unloadComponents } from "@microsoft/applicationinsights-core-js";
import { getDocument, hasDocument, objDeepFreeze, strSubstring, strTrim } from "@nevware21/ts-utils";
import { _DYN_CAPTURE_ALL_META_DAT1, _DYN_DEFAULT_RIGHT_CLICK_2, _DYN_INDEX_OF, _DYN_LENGTH, _DYN_META_DATA_PREFIX, _DYN_PARENT_DATA_TAG, _DYN_TRACK_PAGE_ACTION, _DYN_USE_DEFAULT_CONTENT_0 } from "./__DynamicConstants";
import { BehaviorEnumValidator, BehaviorMapValidator, BehaviorValueValidator, DEFAULT_AI_BLOB_ATTRIBUTE_TAG, DEFAULT_DATA_PREFIX, DEFAULT_DONOT_TRACK_TAG } from "./common/Utils";
import { PageAction } from "./events/PageAction";
import { AutoCaptureHandler } from "./handlers/AutoCaptureHandler";
import { DomContentHandler } from "./handlers/DomContentHandler";
export { BehaviorMapValidator, BehaviorValueValidator, BehaviorEnumValidator };
var defaultValues = objDeepFreeze((_a = {
        autoCapture: true,
        callback: cfgDfMerge({
            pageActionPageTags: cfgDfFunc(),
            pageName: cfgDfFunc(),
            contentName: cfgDfFunc()
        }),
        pageTags: {},
        coreData: cfgDfMerge({
            referrerUri: hasDocument() ? getDocument().referrer : "",
            requestUri: cfgDfString(),
            pageName: cfgDfString(),
            pageType: cfgDfString()
        }),
        dataTags: cfgDfMerge((_b = {},
            _b[_DYN_USE_DEFAULT_CONTENT_0 /* @min:useDefaultContentNameOrId */] = false,
            _b.aiBlobAttributeTag = DEFAULT_AI_BLOB_ATTRIBUTE_TAG,
            _b.customDataPrefix = cfgDfValidate(_dataPrefixChk, DEFAULT_DATA_PREFIX),
            _b[_DYN_CAPTURE_ALL_META_DAT1 /* @min:captureAllMetaDataContent */] = false,
            _b.dntDataTag = DEFAULT_DONOT_TRACK_TAG,
            _b[_DYN_META_DATA_PREFIX /* @min:metaDataPrefix */] = cfgDfString(),
            _b[_DYN_PARENT_DATA_TAG /* @min:parentDataTag */] = cfgDfString(),
            _b)),
        behaviorValidator: cfgDfFunc(function (key) { return key || ""; })
    },
    _a[_DYN_DEFAULT_RIGHT_CLICK_2 /* @min:defaultRightClickBhvr */] = cfgDfString(),
    _a.dropInvalidEvents = false,
    _a.urlCollectHash = false,
    _a.urlCollectQuery = false,
    _a.trackElementTypes = cfgDfString("A,BUTTON,AREA,INPUT"),
    _a));
function _dataPrefixChk(val) {
    return val && val[_DYN_INDEX_OF /* @min:%2eindexOf */](DEFAULT_DATA_PREFIX) === 0;
}
var ClickAnalyticsPlugin = /** @class */ (function (_super) {
    __extends(ClickAnalyticsPlugin, _super);
    function ClickAnalyticsPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "ClickAnalyticsPlugin";
        _this.priority = 181;
        var _config;
        var _pageAction;
        var _autoCaptureHandler;
        var _contentHandler;
        var _autoCapture;
        dynamicProto(ClickAnalyticsPlugin, _this, function (_self, _base) {
            var _identifier = _self.identifier;
            _initDefaults();
            _self.initialize = function (config, core, extensions, pluginChain) {
                if (isNullOrUndefined(core)) {
                    throwError("Error initializing");
                }
                _super.prototype.initialize.call(_this, config, core, extensions, pluginChain);
                _populateDefaults(config);
                // Find the properties plugin.
                var _propertiesExtension;
                arrForEach(extensions, function (extension) {
                    if (extension.identifier === PropertiesPluginIdentifier) {
                        _propertiesExtension = extension;
                    }
                });
                // Append Click Analytics Plugin Version to SDK version.
                if (_propertiesExtension && _propertiesExtension.context && _propertiesExtension.context.internal) {
                    var theVersion = _propertiesExtension.context.internal.sdkVersion;
                    if (theVersion) {
                        theVersion += "_ClickPlugin" + ClickAnalyticsPlugin.Version;
                        if (theVersion[_DYN_LENGTH /* @min:%2elength */] > 64) {
                            theVersion = strTrim(strSubstring(theVersion, 0, 64));
                        }
                        _propertiesExtension.context.internal.sdkVersion = theVersion;
                    }
                }
            };
            _self.processTelemetry = function (env, itemCtx) {
                _self.processNext(env, itemCtx);
            };
            _self[_DYN_TRACK_PAGE_ACTION /* @min:%2etrackPageAction */] = function (pageAction, customProperties) {
                try {
                    _pageAction[_DYN_TRACK_PAGE_ACTION /* @min:%2etrackPageAction */](pageAction, customProperties);
                }
                catch (e) {
                    _throwInternal(_self.diagLog(), 1 /* eLoggingSeverity.CRITICAL */, 103 /* _eInternalMessageId.TrackPageActionEventFailed */, "trackPageAction failed, page action event will not be collected: " + getExceptionName(e), { exception: dumpObj(e) });
                }
            };
            _self._doTeardown = function (unloadCtx, unloadState, asyncCallback) {
                return unloadComponents([
                    _autoCaptureHandler,
                    _contentHandler,
                    _pageAction
                ], unloadCtx, unloadState, function () {
                    _initDefaults();
                    asyncCallback && asyncCallback();
                });
            };
            function _populateDefaults(config) {
                var core = _self.core;
                _self._addHook(onConfigChange(config, function (details) {
                    var config = details.cfg;
                    var ctx = createProcessTelemetryContext(null, config, core);
                    var _config = ctx.getExtCfg(_identifier, defaultValues);
                    var logger = _self.diagLog();
                    _contentHandler = new DomContentHandler(_config, logger);
                    var metaTags = _contentHandler.getMetadata();
                    _pageAction = new PageAction(_self, _config, _contentHandler, _config.callback.pageActionPageTags, metaTags, logger);
                    // Default to DOM autoCapture handler
                    if (_autoCaptureHandler) {
                        _autoCaptureHandler._doUnload();
                    }
                    _autoCaptureHandler = new AutoCaptureHandler(_self, _config, _pageAction, logger);
                    var autoCapture = !!_config.autoCapture;
                    if (!_autoCapture && autoCapture) {
                        _autoCaptureHandler.click();
                    }
                    _autoCapture = autoCapture;
                }));
            }
            function _initDefaults() {
                _config = null;
                _pageAction = null;
                _autoCaptureHandler = null;
                _contentHandler = null;
                _autoCapture = false;
            }
        });
        return _this;
    }
    ClickAnalyticsPlugin.prototype.initialize = function (config, core, extensions, pluginChain) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    ClickAnalyticsPlugin.prototype.processTelemetry = function (env, itemCtx) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Logs a page action event.
     * @param IPageActionTelemetry - The page action event to log.
     * @param customProperties - Additional data used to filter events and metrics. Defaults to empty.
     */
    ClickAnalyticsPlugin.prototype.trackPageAction = function (pageAction, customProperties) {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    ClickAnalyticsPlugin.Version = '3.3.10';
    return ClickAnalyticsPlugin;
}(BaseTelemetryPlugin));
export { ClickAnalyticsPlugin };
//# sourceMappingURL=ClickAnalyticsPlugin.js.map